import type { Campaign } from "@/types/campaign";
import { describeCron } from "./campaignCron";

type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

export const CAMPAIGN_STATUS_TONE: Record<string, Tone> = {
  DRAFT: "neutral",
  ACTIVE: "success",
  INACTIVE: "warning",
};

export const RUN_STATUS_TONE: Record<string, Tone> = {
  QUEUED: "neutral",
  RUNNING: "info",
  COMPLETED: "success",
  PARTIAL: "warning",
  FAILED: "danger",
};

/**
 * What to show for a run. The service labels a finished dispatch PARTIAL
 * whenever anyone was targeted and something failed — including the case
 * where nobody was sent and every contact failed. That reads as FAILED.
 */
export function displayRunStatus(run: {
  status?: string | null;
  sent_count?: number | null;
  failed_count?: number | null;
  targeted_count?: number | null;
  skipped_count?: number | null;
}): string {
  const status = String(run.status ?? "").trim().toUpperCase();
  if (!status || status === "QUEUED" || status === "RUNNING") return status || "—";
  const sent = run.sent_count ?? 0;
  const failed = run.failed_count ?? 0;
  const targeted = run.targeted_count ?? 0;
  const skipped = run.skipped_count ?? 0;
  if (failed > 0 && sent === 0 && (targeted <= 0 || failed + skipped >= targeted)) {
    return "FAILED";
  }
  return status;
}

export const TARGET_TYPE_OPTIONS = [
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "SMS", label: "SMS" },
  { value: "EMAIL", label: "Email" },
];

/**
 * Channel choices for the campaign form. WhatsApp is the only channel the
 * service can send on today, so the others stay visible but unselectable
 * rather than being hidden — the roadmap is worth showing.
 */
export const TARGET_TYPE_FORM_OPTIONS = TARGET_TYPE_OPTIONS.map((option) =>
  option.value === "WHATSAPP"
    ? option
    : { ...option, disabled: true, disabledTooltip: "Coming Soon" },
);

export const SEND_MODE_OPTIONS = [
  { value: "IMMEDIATE", label: "Send immediately" },
  { value: "SCHEDULED_ONCE", label: "Schedule once" },
  { value: "RECURRING", label: "Recurring" },
];

export const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

export const CADENCE_OPTIONS = [
  { value: "HOURLY", label: "Hourly" },
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "CUSTOM", label: "Custom (cron)" },
];

/**
 * Timezone a campaign is scheduled in when none is chosen. The options
 * themselves come from `TIMEZONE_OPTIONS` in `@/types/availability` — the same
 * full IANA list the Settings timezone field offers, so the two agree.
 */
export const DEFAULT_TIMEZONE = "Europe/London";

const LABEL_BY_VALUE = (options: { value: string; label: string }[], value: string | null | undefined) =>
  options.find((o) => o.value === value)?.label ?? value ?? "—";

export const targetTypeLabel = (v: string | null | undefined) => LABEL_BY_VALUE(TARGET_TYPE_OPTIONS, v);
export const sendModeLabel = (v: string | null | undefined) => LABEL_BY_VALUE(SEND_MODE_OPTIONS, v);
export const cadenceLabel = (v: string | null | undefined) => LABEL_BY_VALUE(CADENCE_OPTIONS, v);

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Whether `Intl` can resolve the zone. The service accepts abbreviations like
 * `"IST"` alongside IANA ids, and only the latter are valid here.
 */
function isResolvableZone(timeZone: string | null | undefined): timeZone is string {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Renders a UTC instant as wall-clock time *in the campaign's own timezone* —
 * the frame the schedule was authored in, and the one the service reads times
 * back in. Falls back to browser-local when the zone isn't an IANA id (e.g.
 * `"IST"`), which is the closest honest reading available.
 */
export function isoToDatetimeLocal(iso: string | null | undefined, timeZone?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";

  if (isResolvableZone(timeZone)) {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    const hour = get("hour") === "24" ? "00" : get("hour");
    return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
  }

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/**
 * An instant as `dd/mm/yyyy, hh:mm:ss` read in the campaign's own timezone —
 * the frame the schedule was written in, so every time in the drawer reads the
 * same way the run history does. Falls back to browser-local when the zone is
 * not an IANA id (e.g. `"IST"`), which is the closest honest reading available.
 */
export function formatInstantInZone(
  iso: string | null | undefined,
  timeZone?: string | null,
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const opts: Intl.DateTimeFormatOptions = isResolvableZone(timeZone) ? { timeZone } : {};
  return d.toLocaleString("en-GB", {
    ...opts,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/**
 * Serialises a picker value for the create contract, which takes a zone-less
 * wall-clock string and interprets it in the campaign's `timezone` — so the
 * entered time is passed through verbatim rather than shifted to UTC.
 */
export function datetimeLocalToWallClock(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(trimmed);
  if (!match) return undefined;
  return `${match[1]}T${match[2]}:${match[3]}:00`;
}

/** How far `timeZone` runs ahead of UTC at a given instant, in milliseconds. */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  const hour = get("hour") === 24 ? 0 : get("hour");
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
  return asUtc - instant.getTime();
}

/**
 * Serialises a picker value for the update contract, which takes a UTC instant
 * (`…Z`). The picker holds wall-clock time in the campaign's zone, so the zone
 * offset has to come off first — the offset is resolved twice because the first
 * guess can land on the wrong side of a DST transition.
 *
 * Falls back to browser-local when the zone isn't an IANA id (e.g. `"IST"`).
 */
export function zonedWallClockToUtcIso(
  value: string | null | undefined,
  timeZone: string | null | undefined,
): string | undefined {
  const wallClock = datetimeLocalToWallClock(value);
  if (!wallClock) return undefined;

  if (!isResolvableZone(timeZone)) {
    const local = new Date(wallClock);
    return Number.isNaN(local.getTime()) ? undefined : local.toISOString();
  }

  const asIfUtc = Date.parse(`${wallClock}Z`);
  if (Number.isNaN(asIfUtc)) return undefined;
  let utcMs = asIfUtc - zoneOffsetMs(new Date(asIfUtc), timeZone);
  utcMs = asIfUtc - zoneOffsetMs(new Date(utcMs), timeZone);
  return new Date(utcMs).toISOString();
}

/** Next fire, or "Inactive" when the campaign has been turned off. */
export function nextRunLabel(campaign: Pick<Campaign, "status" | "next_run_at" | "timezone">): string {
  const status = String(campaign.status ?? "").toUpperCase();
  if (status === "INACTIVE") return "Inactive";
  if (status === "DRAFT") return "Draft";
  return formatInstantInZone(campaign.next_run_at, campaign.timezone);
}
export function deliveryProgress(campaign: Campaign): { sent: number; targeted: number; failed: number } | null {
  const summary = campaign.delivery_summary;
  if (!summary) return null;
  return {
    sent: summary.sent_count ?? 0,
    targeted: summary.targeted_count ?? 0,
    failed: summary.failed_count ?? 0,
  };
}

/** Which schedule field actually drives this campaign, for compact list display. */
export function scheduleSummary(campaign: Campaign): string {
  if (campaign.send_mode === "RECURRING") {
    return describeCron(campaign.cadence_cron) || cadenceLabel(campaign.cadence);
  }
  if (campaign.send_mode === "IMMEDIATE") return "Immediate";
  return campaign.scheduled_at ? new Date(campaign.scheduled_at).toLocaleString("en-GB") : "—";
}
